import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarCheck, Clock, AlertCircle, RefreshCw, Send,
  ChevronLeft, ChevronRight, CheckCircle, Lock,
} from 'lucide-react';
import { attendanceAPI, leaveAPI, leaveConfigAPI, employeeAPI } from '../api/apiService';
import './MyAttendance.css';

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const today = new Date();

// Maps leave types to the corresponding balance field
const LEAVE_BALANCE_KEY = {
  CL: 'cl_balance', SL: 'sl_balance', EL: 'el_balance',
  casual: 'cl_balance', sick: 'sl_balance', earned: 'el_balance', annual: 'el_balance',
};

const LEAVE_OPTIONS = [
  { value: 'casual', label: 'Casual Leave (CL)', short: 'CL' },
  { value: 'sick',   label: 'Sick Leave (SL)',   short: 'SL' },
  { value: 'earned', label: 'Earned Leave (EL)', short: 'EL' },
];

// Convert "HH:MM" 24-hour string → "h:MM AM/PM"
const to12h = (t) => {
  if (!t || !/^\d{1,2}:\d{2}/.test(t)) return t || '';
  const [H, M] = t.split(':').map(Number);
  const ap = H >= 12 ? 'PM' : 'AM';
  const h12 = H % 12 === 0 ? 12 : H % 12;
  return `${h12}:${String(M).padStart(2, '0')} ${ap}`;
};

export default function MyAttendance() {
  // Calendar cursor (year/month being browsed)
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });

  // Earliest allowed month = previous month of today. Farthest forward = current month.
  const minMonth = useMemo(() => {
    let my = today.getFullYear(), mm = today.getMonth() - 1;
    if (mm < 0) { mm = 11; my -= 1; }
    return { y: my, m: mm };
  }, []);

  const canGoNext = () => {
    const cy = today.getFullYear(); const cm = today.getMonth();
    return cursor.y < cy || (cursor.y === cy && cursor.m < cm);
  };
  const canGoPrev = () =>
    cursor.y > minMonth.y || (cursor.y === minMonth.y && cursor.m > minMonth.m);

  const goMonth = (delta) => {
    if (delta < 0 && !canGoPrev()) return;
    if (delta > 0 && !canGoNext()) return;
    let y = cursor.y, m = cursor.m + delta;
    if (m < 0)  { m = 11; y -= 1; }
    if (m > 11) { m = 0;  y += 1; }
    setCursor({ y, m });
  };

  return (
    <div className="ma-page">
      <div className="ma-header">
        <div className="ma-header-icon"><CalendarCheck size={20} /></div>
        <div>
          <h1 className="ma-title">My Attendance</h1>
          <p className="ma-subtitle">Click any day to mark attendance, check-in/out times, or apply for leave</p>
        </div>
      </div>

      <MonthlyDashboard
        cursor={cursor}
        goMonth={goMonth}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
      />
    </div>
  );
}

// ── Monthly dashboard with calendar + click-to-edit modal ─────────
function MonthlyDashboard({ cursor, goMonth, canGoPrev, canGoNext }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingDay, setEditingDay] = useState(null);
  const [profile, setProfile] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [toast, setToast] = useState(null);

  // Load employee profile + leave balance once
  useEffect(() => {
    employeeAPI.getMe()
      .then(p => {
        setProfile(p);
        if (p?.employee_id) {
          leaveConfigAPI.getBalance(p.employee_id)
            .then(setLeaveBalance)
            .catch(() => setLeaveBalance(null));
        }
      })
      .catch(() => setProfile(null));
  }, []);

  const load = () => {
    setLoading(true); setError('');
    attendanceAPI.getMyMonthlySummary(cursor.m + 1, cursor.y)
      .then(res => {
        if (typeof res === 'string' || !Array.isArray(res?.days)) {
          setError('Backend endpoint not available — please restart the API service.');
          setData(null);
          return;
        }
        setData(res);
      })
      .catch(err => setError(err.message || 'Failed to load monthly attendance'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [cursor.y, cursor.m]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss toast after 4s
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const summary = data?.summary || {};
  const days = data?.days || [];

  const handleCellClick = (day) => {
    if (day.is_future || day.status_key === 'holiday') return;
    // Scanned days are read-only — barcode scans are the source of truth.
    // Employees can only self-mark days without a scan.
    if (day.from_scan) return;
    setEditingDay(day);
  };

  const handleSaved = (msg) => {
    setEditingDay(null);
    setToast({ type: 'success', msg });
    load();
    // Refresh leave balance if a leave was applied
    if (profile?.employee_id) {
      leaveConfigAPI.getBalance(profile.employee_id).then(setLeaveBalance).catch(() => {});
    }
  };

  return (
    <div className="ma-card ma-dash-card">
      {toast && (
        <div className={`ma-toast ma-toast-${toast.type}`}>
          <CheckCircle size={16} /> {toast.msg}
        </div>
      )}

      <div className="ma-dash-head">
        <div className="ma-dash-nav">
          <button type="button" className="ma-btn-ghost" onClick={() => goMonth(-1)} disabled={!canGoPrev()} title="Previous month">
            <ChevronLeft size={14} />
          </button>
          <h2 className="ma-section-title" style={{ margin: 0 }}>
            {MONTH_LABELS[cursor.m]} {cursor.y}
          </h2>
          <button type="button" className="ma-btn-ghost" onClick={() => goMonth(1)} disabled={!canGoNext()} title="Next month">
            <ChevronRight size={14} />
          </button>
        </div>
        <button type="button" className="ma-btn-ghost" onClick={load} title="Refresh">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Summary chips */}
      <div className="ma-dash-summary">
        <span className="ma-chip ma-chip-present">{summary.present || 0} Present</span>
        <span className="ma-chip ma-chip-leave">{summary.leave || 0} Leave</span>
        {summary.pending > 0 && (
          <span className="ma-chip ma-chip-pending">{summary.pending} Pending</span>
        )}
        <span className="ma-chip ma-chip-holiday">{summary.holiday || 0} Holiday</span>
        <span className="ma-chip ma-chip-weekoff">{summary.weekoff || 0} Week Off</span>
        <span className="ma-chip ma-chip-absent">{summary.absent || 0} Absent</span>
        {leaveBalance && (
          <>
            <span className="ma-chip ma-chip-bal">SL: {leaveBalance.sl_balance ?? 0}</span>
            <span className="ma-chip ma-chip-bal">EL: {leaveBalance.el_balance ?? 0}</span>
            <span className="ma-chip ma-chip-bal">CL: {leaveBalance.cl_balance ?? 0}</span>
          </>
        )}
      </div>

      {loading ? (
        <div className="ma-loading"><div className="ma-spinner" /><p>Loading…</p></div>
      ) : error ? (
        <div className="ma-empty"><AlertCircle size={32} /><p>{error}</p></div>
      ) : (
        <CalendarGrid days={days} year={cursor.y} month={cursor.m} onCellClick={handleCellClick} />
      )}

      {/* Legend */}
      <div className="ma-legend">
        <span><span className="ma-code ma-c-present">P</span> Present</span>
        <span><span className="ma-code ma-c-leave">L</span> Leave</span>
        <span><span className="ma-code ma-c-holiday">H</span> Holiday</span>
        <span><span className="ma-code ma-c-weekoff">WO</span> Week Off</span>
        <span><span className="ma-code ma-c-absent">A</span> Absent</span>
        <span className="ma-legend-tip">Click any day to mark or apply leave</span>
      </div>

      {editingDay && (
        <MarkDayModal
          day={editingDay}
          profile={profile}
          leaveBalance={leaveBalance}
          monthDays={days}
          onClose={() => setEditingDay(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// Calendar grid — 7-col wall calendar, cells are buttons that open the mark modal.
function CalendarGrid({ days, year, month, onCellClick }) {
  const WEEK_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const CODE_MAP = { present: 'P', leave: 'L', holiday: 'H', weekoff: 'WO', absent: 'A' };

  const firstWeekday = new Date(year, month, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push({ blank: true, key: `b${i}` });
  days.forEach(d => cells.push({ ...d, key: d.date }));
  while (cells.length % 7 !== 0) cells.push({ blank: true, key: `bt${cells.length}` });

  const shortLeave = (label) => {
    const s = (label || '').toLowerCase();
    if (s.includes('sick'))   return 'SL';
    if (s.includes('casual')) return 'CL';
    if (s.includes('earned') || s.includes('annual')) return 'EL';
    return 'L';
  };

  const isLeaveStatus = (k) => k === 'leave' || k === 'leave-pending';

  return (
    <div className="ma-cal2-wrap">
      <div className="ma-cal2-headrow">
        {WEEK_LABELS.map(w => <div key={w} className="ma-cal2-headcell">{w}</div>)}
      </div>
      <div className="ma-cal2-grid">
        {cells.map(c => {
          if (c.blank) return <div key={c.key} className="ma-cal2-cell ma-cal2-blank" />;
          const code = isLeaveStatus(c.status_key)
            ? shortLeave(c.status_label)
            : (CODE_MAP[c.status_key] || '');
          const codeCls = c.status_key === 'leave-pending' ? 'leave' : c.status_key;
          const editable = !c.is_future && c.status_key !== 'holiday' && !c.from_scan;
          return (
            <button
              type="button"
              key={c.key}
              onClick={() => editable && onCellClick?.(c)}
              disabled={!editable}
              className={`ma-cal2-cell ma-cal2-${c.status_key || 'none'}${c.is_today ? ' ma-cal2-today' : ''}${c.is_future ? ' ma-cal2-future' : ''}${editable ? ' ma-cal2-clickable' : ''}${c.from_scan ? ' ma-cal2-scanned' : ''}`}
              title={[
                `${c.date} (${c.day_of_week})`,
                c.status_label,
                c.check_in && `In: ${to12h(c.check_in)}`,
                c.check_out && `Out: ${to12h(c.check_out)}`,
                c.hours != null && `${c.hours} hours`,
                c.notes,
                c.from_scan && 'Recorded via barcode scan — not editable',
                editable && 'Click to edit',
              ].filter(Boolean).join(' · ')}
            >
              <div className="ma-cal2-top">
                <span className="ma-cal2-daynum">{c.day}</span>
                {c.from_scan && (
                  <span className="ma-cal2-scanmark" aria-label="Scanned"><Lock size={9} /></span>
                )}
                {code && (
                  <span className={`ma-code ma-c-${codeCls}`}>
                    {code}{c.is_half_day ? '½' : ''}
                  </span>
                )}
              </div>
              {(c.check_in || c.check_out) && (
                <div className="ma-cal2-times">
                  {c.check_in ? to12h(c.check_in) : '—'} <span className="ma-cal2-arrow">→</span> {c.check_out ? to12h(c.check_out) : '—'}
                </div>
              )}
              {c.status_key === 'leave-pending' && (
                <div className="ma-cal2-sub ma-cal2-pending-sub">Pending</div>
              )}
              {c.status_key === 'holiday' && c.notes && (
                <div className="ma-cal2-sub">{c.notes}</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Friendly time picker (Hour / Minute / AM-PM segmented dropdowns) ─
function TimePicker({ value, onChange }) {
  // value is "HH:MM" in 24h (or empty)
  const parse = (v) => {
    if (!v || !/^\d{1,2}:\d{2}$/.test(v)) return { h12: '', mm: '', ap: 'AM' };
    const [H, M] = v.split(':').map(Number);
    const ap = H >= 12 ? 'PM' : 'AM';
    const h12 = H % 12 === 0 ? 12 : H % 12;
    return { h12: String(h12), mm: String(M).padStart(2, '0'), ap };
  };
  const { h12, mm, ap } = parse(value);

  const emit = (nh, nm, na) => {
    if (!nh || nm === '') { onChange(''); return; }
    let H = parseInt(nh, 10) % 12;
    if (na === 'PM') H += 12;
    onChange(`${String(H).padStart(2, '0')}:${nm}`);
  };

  return (
    <div className="ma-timepick">
      <select className="ma-timepick-seg" value={h12} onChange={e => emit(e.target.value, mm || '00', ap)}>
        <option value="" disabled>HH</option>
        {Array.from({ length: 12 }, (_, i) => String(i + 1)).map(h => (
          <option key={h} value={h}>{h.padStart(2, '0')}</option>
        ))}
      </select>
      <span className="ma-timepick-sep">:</span>
      <select className="ma-timepick-seg" value={mm} onChange={e => emit(h12 || '12', e.target.value, ap)}>
        <option value="" disabled>MM</option>
        {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      <div className="ma-timepick-ampm" role="group" aria-label="AM or PM">
        <button
          type="button"
          className={`ma-timepick-ampbtn ${ap === 'AM' ? 'is-active' : ''}`}
          onClick={() => emit(h12 || '12', mm || '00', 'AM')}
        >AM</button>
        <button
          type="button"
          className={`ma-timepick-ampbtn ${ap === 'PM' ? 'is-active' : ''}`}
          onClick={() => emit(h12 || '12', mm || '00', 'PM')}
        >PM</button>
      </div>
    </div>
  );
}

// ── Modal: mark attendance OR apply leave for a specific day ────
function MarkDayModal({ day, profile, leaveBalance, monthDays, onClose, onSaved }) {
  const initialStatus = (() => {
    if (day.status_key === 'leave') return 'Leave';
    if (day.status_key === 'absent') return day.status_label === 'LWP' ? 'LOP' : 'Absent';
    if (day.status_key === 'present') return 'Present';
    return 'Present';
  })();

  const [status, setStatus]     = useState(initialStatus);
  // Present-specific
  const [checkIn, setCheckIn]   = useState(day.check_in || '');
  const [checkOut, setCheckOut] = useState(day.check_out || '');
  // Leave-specific (full apply form)
  const [leaveType, setLeaveType] = useState('casual');
  const [startDate, setStartDate] = useState(day.date);
  const [endDate, setEndDate]   = useState(day.date);
  const [halfDay, setHalfDay]   = useState(false);
  // Shared
  const [remarks, setRemarks]   = useState(day.notes || '');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  // Hours preview from check-in/out
  const derivedHours = useMemo(() => {
    if (!checkIn || !checkOut) return null;
    const [h1, m1] = checkIn.split(':').map(Number);
    const [h2, m2] = checkOut.split(':').map(Number);
    if ([h1, m1, h2, m2].some(isNaN)) return null;
    const diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    return diff > 0 ? (diff / 60).toFixed(2) : null;
  }, [checkIn, checkOut]);

  // Leave days count
  const leaveDays = useMemo(() => {
    if (!startDate || !endDate || startDate > endDate) return 0;
    if (halfDay && startDate === endDate) return 0.5;
    return Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1;
  }, [startDate, endDate, halfDay]);

  const leaveBalKey = LEAVE_BALANCE_KEY[leaveType];
  const availableBalance = leaveBalKey && leaveBalance
    ? parseFloat(leaveBalance[leaveBalKey] || 0)
    : null;
  const balanceInsufficient = availableBalance !== null && availableBalance < leaveDays;

  const fmtFull = new Date(day.date + 'T00:00:00').toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const genReqNumber = () => {
    const y = new Date().getFullYear();
    const r = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
    return `LVR-${y}-${r}`;
  };

  const handleSave = async () => {
    setErr('');
    setSaving(true);
    try {
      if (status === 'Leave') {
        if (!profile?.employee_id) {
          throw new Error('Could not load your employee profile — please refresh and try again.');
        }
        if (availableBalance !== null && availableBalance <= 0) {
          throw new Error(`No ${leaveType} leave balance available.`);
        }
        if (balanceInsufficient) {
          throw new Error(`Insufficient balance — you have ${availableBalance} day(s) of ${leaveType} leave but requested ${leaveDays}.`);
        }
        if (!remarks?.trim()) {
          throw new Error('Please enter a reason for leave.');
        }
        // Guard against duplicate submissions: any day in the requested range
        // that is already showing a pending leave on the calendar.
        const pendingClash = (monthDays || []).find(d =>
          d.is_pending && d.date >= startDate && d.date <= endDate
        );
        if (pendingClash) {
          throw new Error(`You already have a pending leave on ${pendingClash.date}. Wait for it to be approved or rejected before applying again.`);
        }
        const reqNo = genReqNumber();
        await leaveAPI.apply({
          leaveRequestNumber: reqNo,
          employeeId:         profile.employee_id,
          employeeName:       `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
          establishmentId:    profile.establishment_id || null,
          departmentId:       profile.department || null,
          managerId:          'MANAGER-001',
          approverId:         'MANAGER-001',
          approverName:       'Manager',
          leaveType:          leaveType,
          startDate:          startDate,
          endDate:            endDate,
          daysCount:          leaveDays,
          halfDay:            halfDay,
          reason:             remarks.trim(),
          remarks:            null,
          leaveStatus:        'pending',
        });
        onSaved(`Leave application submitted — ${reqNo}. Pending approval.`);
      } else {
        const payload = {
          attendance_date: day.date,
          status,
          remarks: remarks.trim() || null,
        };
        if (status === 'Present') {
          payload.check_in_time = checkIn || null;
          payload.check_out_time = checkOut || null;
          payload.hours_worked = derivedHours ? Number(derivedHours) : null;
        }
        await attendanceAPI.markMyAttendance(payload);
        onSaved(`Marked as ${status} for ${day.date}.`);
      }
    } catch (e) {
      setErr(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ma-modal-backdrop" onClick={onClose}>
      <div className="ma-modal" onClick={e => e.stopPropagation()}>
        <div className="ma-modal-head">
          <div>
            <div className="ma-modal-title">{status === 'Leave' ? 'Apply Leave' : 'Mark Attendance'}</div>
            <div className="ma-modal-sub">{fmtFull}</div>
          </div>
          <button type="button" className="ma-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="ma-modal-body">
          {/* Status pills */}
          <div className="ma-modal-field">
            <label className="ma-label">What are you marking?</label>
            <div className="ma-modal-pills">
              {['Present', 'Leave', 'Absent', 'LOP'].map(s => (
                <button
                  type="button"
                  key={s}
                  className={`ma-modal-pill ${status === s ? 'active' : ''} ma-pill-${s.toLowerCase()}`}
                  onClick={() => setStatus(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Present → check-in/out */}
          {status === 'Present' && (
            <>
              <div className="ma-modal-row">
                <div className="ma-modal-field">
                  <label className="ma-label">Check In</label>
                  <TimePicker value={checkIn} onChange={setCheckIn} />
                </div>
                <div className="ma-modal-field">
                  <label className="ma-label">Check Out</label>
                  <TimePicker value={checkOut} onChange={setCheckOut} />
                </div>
              </div>
              {derivedHours && (
                <div className="ma-modal-hours">Hours worked: <strong>{derivedHours}h</strong></div>
              )}
            </>
          )}

          {/* Leave → leave application form */}
          {status === 'Leave' && (
            <>
              <div className="ma-modal-field">
                <label className="ma-label">Leave Type <span style={{ color: '#ef4444' }}>*</span></label>
                <select className="ma-input" value={leaveType} onChange={e => setLeaveType(e.target.value)}>
                  {LEAVE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                      {leaveBalance ? ` — ${leaveBalance[LEAVE_BALANCE_KEY[o.value]] ?? 0} day(s) available` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ma-modal-row">
                <div className="ma-modal-field">
                  <label className="ma-label">From <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="date" className="ma-input"
                    value={startDate}
                    onChange={e => {
                      const v = e.target.value;
                      setStartDate(v);
                      if (v && endDate && v > endDate) setEndDate(v);
                    }}
                  />
                </div>
                <div className="ma-modal-field">
                  <label className="ma-label">To <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="date" className="ma-input"
                    value={endDate} min={startDate}
                    onChange={e => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <label className="ma-modal-halfday">
                <input
                  type="checkbox"
                  checked={halfDay}
                  onChange={e => setHalfDay(e.target.checked)}
                  disabled={startDate !== endDate}
                />
                <span>Half day (only for single-day leave)</span>
              </label>

              <div className="ma-modal-leaveinfo">
                <span>Total days: <strong>{leaveDays}</strong></span>
                {availableBalance !== null && (
                  <span className={balanceInsufficient ? 'ma-bal-bad' : 'ma-bal-ok'}>
                    Available: <strong>{availableBalance}</strong>
                  </span>
                )}
              </div>

              {balanceInsufficient && (
                <div className="ma-banner ma-banner-error">
                  Insufficient balance. You have {availableBalance} day(s) but requested {leaveDays}.
                </div>
              )}
            </>
          )}

          <div className="ma-modal-field">
            <label className="ma-label">
              {status === 'Leave' ? <>Reason <span style={{ color: '#ef4444' }}>*</span></> : <>Remarks <span className="ma-optional">(optional)</span></>}
            </label>
            <textarea
              className="ma-input ma-textarea"
              rows={2} maxLength={240}
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder={status === 'Leave' ? 'Briefly describe the reason for leave…' : 'Any note for your manager…'}
            />
          </div>

          {err && <div className="ma-banner ma-banner-error">{err}</div>}
        </div>

        <div className="ma-modal-foot">
          <button type="button" className="ma-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="ma-btn-primary"
            onClick={handleSave}
            disabled={saving || (status === 'Leave' && balanceInsufficient)}
          >
            {saving
              ? <><Clock size={14} /> Saving…</>
              : <><Send size={14} /> {status === 'Leave' ? 'Submit Leave Application' : 'Save'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
